<template>
  <section ref="wrap" class="header-search">
    <header>
      <input
        v-model="query"
        placeholder="O que você procura?"
        @input="search($event.target.value)"
        @keydown.esc.prevent="closeSearch(true)"
      />
      <div>
        <fa :icon="['fal', 'magnifying-glass']" class="icon" v-if="!query || query.length < 3"/>
        <fa :icon="['fal', 'broom-wide']" class="icon" @click="closeSearch(true)" v-else/>
      </div>
    </header>
    <div class="resultWrap" :class="{ active: active }" :style="{ width: boxW + 'px'}">
      <span class="loader" v-if="working"></span>
      <span v-else-if="results.length == 0" class="emptyMessage">
        <p>Nenhum resultado encontrado</p>
      </span>
      <ol v-else>
        <li v-for="result in results" :key="result.key">
          <a :href="menuHref(result)" @click="goToRoute(result, $event)" v-if="result.type === 'menu'">
            <fa :icon="['far','square-poll-vertical']" class="icon painel" title="Painel" v-if="result.dashboard || isVuePanel(result)"></fa>
            <fa :icon="['far','arrow-up-right-from-square']" class="icon externo" title="Link Externo" v-else-if="result.urlType === 'blank' || (result.external && !result.dashboard)"></fa>
            <fa :icon="['far','link-simple']" class="icon apollo" title="Apollo" v-else></fa>
            <div>
              <h6>{{ result.name }}</h6>
            </div>
          </a>
          <div
            v-else
            :class="{
              personResult: result.type === 'person',
              personResultActionable: isPersonResultActionable(result),
              collaboratorResultWorking: collaboratorDetailsWorking !== null && collaboratorDetailsWorking === collaboratorId(result)
            }"
            :role="isPersonResultActionable(result) ? 'link' : null"
            :tabindex="isPersonResultActionable(result) ? 0 : null"
            @click="openPersonResult(result, $event)"
            @keydown.enter.self.prevent="openPersonResult(result, $event)"
            @keydown.space.self.prevent="openPersonResult(result, $event)"
          >
            <span v-if="result.collaborator" class="apollo">
              <fa :icon="['far','user-tie-hair']" class="icon" title="Colaborador" ></fa>
              <p>Colaborador</p>
            </span>
            <span v-if="result.student" class="painel">
              <fa :icon="['far','user-graduate']" class="icon" title="Aluno" ></fa>
              <p>Aluno</p>
              <small v-if="hasDuplicateStudentProfiles(result)" class="duplicateStudentBadge">
                <fa :icon="['far', 'triangle-exclamation']" />
                Cadastro duplicado ({{ studentProfiles(result).length }})
              </small>
            </span>
            <span v-if="!result.student && !result.collaborator" class="externo">
              <fa :icon="['far','user']" class="icon" title="Pessoa" ></fa>
              <p v-if="result.students && result.students.length > 0">Responsável</p>
              <p v-else>Pessoa</p>
            </span>
            <div>
              <template v-if="result.collaborator">
                <h6 class="name">{{ result.name.toLowerCase() }}</h6>
                <p>{{ result.collaborator.role || query }}</p>
                <a v-if="result.collaborator.email || result.email" :href="'mailto:' + (result.collaborator.email || result.email)" class="emailLink" @click.stop.prevent="copyEmail(result.collaborator.email || result.email)">
                  <fa :icon="['fal','paper-plane']" class="icon"></fa>
                  {{ result.collaborator.email || result.email }}
                </a>
                <span class="detailsHint">
                  <fa :icon="['fal', 'arrow-right-long']" />
                  {{ personActionLabel(result) }}
                </span>
              </template>
              <template v-else-if="result.student">
                <h6 class="name">{{ result.name.toLowerCase() }}</h6>
                <p>{{ result.student.unit }}</p>
                <span class="detailsHint">
                  <fa :icon="['fal', 'arrow-right-long']" />
                  {{ personActionLabel(result) }}
                </span>
              </template>
              <template v-else>
                <a :href="routeHref(createCentralRoute('responsavel', result))" class="pessoaLink" @click.stop="navigateToPerson('responsavel', result, $event)">
                  <h6 class="name">{{ result.name.toLowerCase() }}</h6>
                  <span class="detailsHint">
                    <fa :icon="['fal', 'arrow-right-long']" />
                    {{ personActionLabel(result) }}
                  </span>
                </a>
              </template>
            </div>
            <div v-if="hasDuplicateStudentProfiles(result)" class="subList studentProfileList" @click.stop>
              <p class="studentProfileNotice">Escolha qual cadastro de aluno deseja consultar:</p>
              <ul>
                <li v-for="profile in studentProfiles(result)" :key="profile.idStudent">
                  <a
                    :href="routeHref(createCentralRoute('aluno-perfil', profile))"
                    @click.stop="navigateToPerson('aluno-perfil', profile, $event)"
                  >
                    <span class="studentProfileIdentity">
                      <strong>{{ profile.rm ? `RM ${profile.rm}` : `Aluno ${profile.idStudent}` }}</strong>
                      <small>{{ studentProfileContext(profile) }}</small>
                    </span>
                    <span class="studentProfileAction">
                      Abrir
                      <fa :icon="['fal', 'arrow-right-long']" />
                    </span>
                  </a>
                </li>
              </ul>
            </div>
            <div v-if="result.students && result.students.length > 0" class="subList">
              <ul>
                <li v-for="student in result.students" :key="student.idStudent">
                  <a :href="routeHref(createCentralRoute('responsavel-aluno', student))" class="name" @click.stop="navigateToPerson('responsavel-aluno', student, $event)">
                    <span>{{ student.name.toLowerCase() }}</span>
                    <fa :icon="['fal', 'arrow-right-long']"></fa>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </li>
      </ol>
    </div>
    <div class="backdrop" @click="closeSearch(true)" v-if="active"></div>
    <modal ref="collaboratorModal" wrapped>
      <collaborator-details
        v-if="collaboratorDetails"
        :details="collaboratorDetails"
        @close="$refs.collaboratorModal.close()"
      />
    </modal>
  </section>
</template>

<script>
import SearchService from  '@/services/search.service.js'
import UserService from '@/services/user.service.js'
import Modal from '@/components/utilities/Modal.vue'
import CollaboratorDetails from '@/components/utilities/CollaboratorDetails.vue'

export default {
  components: {
    Modal,
    CollaboratorDetails
  },
  data () {
    return {
      active: false,
      boxW: window.innerWidth < 1024 ? window.innerWidth - 60 : 354,
      query: null,
      results: [],
      searchWait: null,
      searchRequestController: null,
      searchSequence: 0,
      working: false,
      collaboratorDetails: null,
      collaboratorDetailsWorking: null,
      currentUser: localStorage.getItem('profile') ? JSON.parse(atob(localStorage.getItem('profile'))) : null
    }
  },
  mounted () {

  },
  beforeUnmount () {
    this.cancelPendingSearch()
  },
  methods: {
    copyEmail(email) {
      if (!email) return;

      navigator.clipboard.writeText(email)
        .then(() => {
          window.toaster.emit('open', {
            style: 'success',
            message: 'E-mail copiado para a área de transferência!',
            floater: true
          });
        })
        .catch(() => {
          window.toaster.emit('open', {
            style: 'error',
            message: 'Erro ao copiar e-mail.',
            floater: true
          });
        });
    },
    collaboratorId ( result ) {
      return result?.collaborator?.idCollaborator || null
    },
    personActionLabel ( result ) {
      if (result.collaborator?.canViewDetails) return 'Ver informações'
      if (this.hasDuplicateStudentProfiles(result)) return 'Escolha um cadastro abaixo'
      if (result.student) return 'Abrir Central do Aluno'
      if (result.students?.length) return 'Abrir responsável'
      return 'Pesquisar pessoa'
    },
    openPersonResult ( result, event ) {
      if (result.collaborator?.canViewDetails) {
        this.openCollaboratorDetails(result)
        return
      }

      if (result.student) {
        if (this.hasDuplicateStudentProfiles(result)) return
        this.navigateToPerson('aluno', result, event)
        return
      }

      this.navigateToPerson('responsavel', result, event)
    },
    openCollaboratorDetails ( result ) {
      if (!result.collaborator?.canViewDetails) return

      const idColaborador = this.collaboratorId(result)
      if (!idColaborador || this.collaboratorDetailsWorking) return

      this.collaboratorDetailsWorking = idColaborador

      SearchService.getCollaboratorDetails(idColaborador).then(
        response => {
          this.collaboratorDetails = response.data
          this.closeSearch()
          this.$refs.collaboratorModal.open({
            title: 'Informações do colaborador',
            titleAlign: 'left',
            message: '',
            size: 'large',
            backdrop: true,
            backdropColor: 'blur',
            backdropClose: true,
            fullHeight: false,
            padding: '20px',
            onClose: () => { this.collaboratorDetails = null }
          })
        },
        error => {
          window.toaster.emit('open', {
            style: 'error',
            message: error.response?.data?.message || 'Não foi possível carregar os dados do colaborador.',
            floater: true
          })
        }
      ).finally(() => {
        this.collaboratorDetailsWorking = null
      })
    },
    toggleSearch () {
      // const componentW = this.$refs.wrap
      // console.log("componentW:", componentW)

      this.active = true

    },
    search ( value ) {
      const query = (value || '').trim()

      this.cancelPendingSearch()
      const sequence = ++this.searchSequence

      if ( query.length < 3 ) {
        this.closeSearch()
        return
      }

      this.toggleSearch()
      this.searchWait = setTimeout(() => {
        this.searchWait = null
        const controller = new AbortController()
        this.searchRequestController = controller
        this.working = true

        SearchService.ApolloSearch(query, controller.signal).then(
          response => {
            if ( sequence !== this.searchSequence || controller.signal.aborted ) return

            this.results = Array.isArray(response.data?.items) ? response.data.items : []
          },
          error => {
            if ( error?.code === 'ERR_CANCELED' || controller.signal.aborted ) return

            this.results = []
          }
        ).finally(() => {
          if ( sequence !== this.searchSequence ) return

          this.working = false
          this.searchRequestController = null
        })
      }, 500)
    },
    cancelPendingSearch () {
      if ( this.searchWait ) {
        clearTimeout(this.searchWait)
        this.searchWait = null
      }

      if ( this.searchRequestController ) {
        this.searchRequestController.abort()
        this.searchRequestController = null
      }
    },
    clearData () {
      
    },
    closeSearch ( clear ) {
      this.cancelPendingSearch()
      this.searchSequence++
      this.working = false
      this.active = false
      this.results = []

      if ( clear ) this.query = ""
    },
    isVuePanel ( item ) {
      return item?.urlType === 'vue-panel'
    },
    isModifiedClick ( event ) {
      return event && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.button != null && event.button !== 0))
    },
    routeHref ( route ) {
      const target = this.normalizeInternalRoute(route)
      return target ? this.$router.resolve(target).href : null
    },
    menuHref ( item ) {
      if (this.isVuePanel(item)) return this.routeHref(item.route)
      if ((item.route && item.urlType === 'blank') || (item.external && !item.dashboard)) return item.route
      if (item.external && item.dashboard) {
        return this.routeHref({name: 'Painel', params: { id: item.dashboard }, query: {path: item.route}})
      }
      return this.routeHref(item.route)
    },
    async goToRoute ( item, event ) {
      if (this.isModifiedClick(event)) return
      event?.preventDefault()

      if ( this.isVuePanel(item) ) {
        if ( await this.navigateToRoute(item.route) ) this.saveRotaFavorita(item)
      } else if ( (item.route && item.urlType === 'blank') || (item.external && !item.dashboard) ) {
        window.open(item.route)
        this.closeSearch()
      } else if ( item.external && item.dashboard ) {
        const route = {name: 'Painel', params: { id: item.dashboard }, query: {path: item.route} }
        if ( await this.navigateToRoute(route) ) this.saveRotaFavorita(item)
      } else {
        if ( await this.navigateToRoute(item.route) ) this.saveRotaFavorita(item)
      }
    },
    async navigateToPerson ( target, person, event ) {
      const route = this.createCentralRoute(target, person)
      if (!route) return

      await this.navigateToRoute(route, event)
    },
    normalizeInternalRoute ( route ) {
      if ( typeof route !== 'string' ) return route

      const normalizedRoute = route.trim()
      if ( !normalizedRoute ) return null

      return normalizedRoute.startsWith('/') ? normalizedRoute : `/${normalizedRoute.replace(/^\.\//, '')}`
    },
    async navigateToRoute ( route, event ) {
      const target = this.normalizeInternalRoute(route)
      if ( !target ) {
        this.showNavigationError()
        return false
      }

      try {
        if (this.isModifiedClick(event)) {
          // Links keep the browser's native behavior; clickable rows need an explicit new tab.
          if (event.currentTarget?.tagName !== 'A') {
            window.open(this.$router.resolve(target).href, '_blank', 'noopener')
          }
          return false
        }

        event?.preventDefault()
        await this.$router.push(target)
        this.closeSearch()
        return true
      } catch ( error ) {
        console.error('Erro ao navegar pela busca global:', error)
        this.showNavigationError()
        return false
      }
    },
    showNavigationError () {
      window.toaster.emit('open', {
        style: 'error',
        message: 'Não foi possível abrir este resultado. Tente novamente.',
        floater: true
      })
    },
    createCentralRoute ( target, person ) {
      if ( target == 'aluno' ) {
        const route = `/centraldoaluno/${person.student.idStudent}/${person.student.idPessoa}`
        return route
      }

      if ( target == 'responsavel' ) {
        const responsavel = encodeURIComponent(person.name)
        var tipo = 0
        
        if ( person.students && person.students.length > 0 ) {
          if ( person.students[0].financial ) {
            tipo = 2
          } else if ( person.students[0].pedagogical ) {
            tipo = 3
          }
        }
        
        const route = `/matriculas/pesquisa/?nome=${responsavel}&tipoNome=${tipo}`
        return route
      }

      if ( target == 'responsavel-aluno' ) {
        const route = `/centraldoaluno/${person.idStudent}/${person.idPessoa}`
        return route
      }

      if ( target == 'aluno-perfil' ) {
        const route = `/centraldoaluno/${person.idStudent}/${person.idPessoa}`
        return route
      }
    },
    studentProfiles ( result ) {
      if (Array.isArray(result?.studentProfiles) && result.studentProfiles.length) {
        return result.studentProfiles
      }

      return result?.student ? [result.student] : []
    },
    hasDuplicateStudentProfiles ( result ) {
      return this.studentProfiles(result).length > 1
    },
    isPersonResultActionable ( result ) {
      return !!result?.collaborator?.canViewDetails || !this.hasDuplicateStudentProfiles(result)
    },
    studentProfileContext ( profile ) {
      return [profile.unit, profile.period, profile.series]
        .filter(Boolean)
        .join(' · ') || 'Sem matrícula vinculada'
    },
    saveRotaFavorita ( route ) {
      const idPessoa = this.currentUser.idPessoa
      const data = {
        idPessoa: idPessoa,
        idRole: null,
        idPermission: route.id
      }

      UserService.saveRotaFavorita(data).then(
        response => {
          console.log("saveRotaFavorita:", response.data)
        }
      )
    },
  }
}
</script>

<style lang="scss" scoped>
  .header-search {
    width: 100%; height: 59px;

    p {
      margin: 0; padding: 0;
    }
  }
  .backdrop {
    position: fixed; top: 0; left: 0;
    width: 100vw; height: 100vh;
    z-index: 100;
  }

  header {
    width: 100%; height: 100%;
    position: relative;
    z-index: 200;
    padding: 0; margin: 0;

    >div {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: $mg_mini;
      position: absolute;
      top: 50%; transform: translateY(-50%); right: $hmg_mid;
      font-size: 18px;
      color: $color-primary;
    }

    input {
      width: 100%; height: 100%;
      padding: 0 $hmg_mid;
      border: 0; outline: 0;
      box-shadow: none;
      vertical-align: middle;
      color: $color-text;
      background-color: transparent;

      &::placeholder {
        color: $color-text;
        opacity: .5;
      }
    }
  }

  .resultWrap{
    position: fixed;
    top: 60px; height: 0;
    right: 0; overflow-y: auto;
    @extend %transition_6e;
    @extend %scrollbar_light;
    background-color: $color-secondary;
    border-left: $border-component;
    z-index: 1000;

    &.active {
      height: calc(100vh - 60px);
      border-top: $border-component;

      @media screen and ( max-width: 640px ) {
        height: calc(100vh - 120px);
      }
    }

    ol {
      padding: 0; margin: 0;

      >li {
        list-style: none;
        margin: 0;
        border-bottom: $border-component;

        
        > a,
        > div {
          display: block;
          padding: $hmg_mid;

          &:hover {
            @extend %transition_4o;
            background-color: $color-bg;
          }
          > div {
            margin-top: $mg_mini;
          }
          > p {
            margin: 0; padding: 0;
          }

          >span {
            display: flex; align-content: center;
            p {
              margin: 0 0 0 $hmg_mini; padding: 0;
              font-size: $font-size-small;
            }

            &.painel {
              color: $color-painel
            }
            &.externo {
              color: $color-externo
            }
            &.apollo {
              color: $color-apollo
            }
            .duplicateStudentBadge {
              display: inline-flex;
              align-items: center;
              gap: $hmg_mini;
              margin-left: auto;
              padding: $hmg_mini $hmg_small;
              border-radius: $border-radius;
              background-color: $color-warning;
              color: $color-text;
              font-size: $font-size-small;
              font-weight: 600;
            }
            .icon {
              font-size: 14px
            }
          }
        }
        
      }
    }
  }

  .personResult {
    .pessoaLink {
      display: block;
    }

    .detailsHint {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-top: $hmg_small;
      color: $color-apollo;
      font-size: $font-size-small;
    }
  }

  .personResultActionable {
    cursor: pointer;

    &:focus,
    &:focus-visible {
      outline: 2px solid $color-primary;
      outline-offset: -2px;
    }

    &:hover .name,
    &:focus .name {
      color: $color-primary;
    }
  }

  .collaboratorResultWorking {
    pointer-events: none;
    opacity: .65;
  }

  .loader {
    width: 100%;
    height: 6px;
    display: block;
    position: relative;
    overflow: hidden;
    background-color: $color-bg;
  }
  .loader::after {
    content: '';  
    width: 96px; height: 6px;
    background-color: $color-primary;
    position: absolute; top: 0; left: 0;
    box-sizing: border-box;
    animation: hit 0.6s ease-in-out infinite alternate;
  }

  @keyframes hit {
    0% {
      left: 0;
      transform: translateX(-1%);
    }
    100% {
      left: 100%;
      transform: translateX(-99%);
    }
  }

  .emptyMessage { 
    padding: $hmg_small
  }

  .name {
    text-transform: capitalize
  }
  .emailLink { 
    display: flex; align-content: center;
    margin-top: $mg_mini;
    .icon {
      margin-right: $hmg_mini
    }

    &:hover {
      color: $color-primary
    }
  }

  .subList {
    padding: 0; margin: 0;
    width: 100%;

    ul {
      padding: 0; margin: $mg_mini 0 0 0;

      li {  
        padding: 0; margin: 0;
        border-bottom: $border-component;
        list-style: none;

        a { 
          display: flex; align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: $hmg_mini 0;
          @extend %transition_4e;

          &:hover {
            padding: $hmg_mini;
          }
        }
      }
    }
  }

  .studentProfileList {
    margin-top: $hmg_small;
    border-top: $border-component;

    .studentProfileNotice {
      padding-top: $hmg_small;
      color: $color-text;
      font-size: $font-size-small;
      font-weight: 600;
    }

    li:last-child {
      border-bottom: 0;
    }

    a {
      min-height: 44px;
      gap: $hmg_small;
    }

    .studentProfileIdentity {
      min-width: 0;

      strong,
      small {
        display: block;
      }

      small {
        margin-top: 2px;
        color: $color-text;
        opacity: .7;
      }
    }

    .studentProfileAction {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      gap: $hmg_mini;
      color: $color-apollo;
      font-size: $font-size-small;
      font-weight: 600;
    }
  }
</style>
